import { useEffect, useRef, useState } from 'react';
import { useBloco, useMapa } from '../content';
import { useReveal } from '../hooks/useReveal';
import BRAZIL_SVG_PATHS from './BrazilMapPaths';

function xy(lon, lat) {
  return [18.0167 * lon + 1303.1484, -17.4273 * lat + 99.9988];
}

function getPathBBox(dStr) {
  const nums = [...dStr.matchAll(/([\d.]+),([\d.]+)/g)];
  if (!nums.length) return null;
  const xs = nums.map(m => parseFloat(m[1]));
  const ys = nums.map(m => parseFloat(m[2]));
  return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
}

// Painel de zoom lateral - SVG isolado do estado com cidades
function ZoomPanel({ zoomState }) {
  if (!zoomState) {
    return (
      <div className="map-zoom-empty">
        <div className="map-zoom-hint">
          <span className="map-zoom-hint-icon">↗</span>
          <span>Passe o mouse ou toque<br />em um estado para ver<br />as cidades</span>
        </div>
      </div>
    );
  }

  const { d, cities, bbox } = zoomState;
  if (!bbox) return null;

  const pad = Math.max(bbox.w, bbox.h) * 0.2;
  const vb = `${bbox.x - pad} ${bbox.y - pad} ${bbox.w + pad * 2} ${bbox.h + pad * 2}`;
  // RO bbox ≈ 90 user units; mantém o tamanho visual das labels igual ao de Rondônia em todos os estados
  const RO_REF = 90;
  const scale = Math.max(bbox.w, bbox.h) / RO_REF;
  const fontPx = 3.5 * scale;
  const dotR = 1 * scale;
  const hqR = 2.5 * scale;
  const hqRingR = 6 * scale;
  const hqStroke = 1.5 * scale;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={vb}
      style={{ width: '100%', height: '100%', overflow: 'visible', animation: 'mapZoomIn .4s cubic-bezier(.2,.8,.2,1)' }}
    >
      {/* Sombra do estado */}
      <path d={d} fill="rgba(12,26,46,.05)" stroke="none" transform="translate(4,6)" />
      {/* Estado preenchido */}
      <path d={d} fill="rgba(242,107,34,.12)" stroke="#F26B22" strokeWidth="1.5" strokeLinejoin="round" />

      {/* Cidades */}
      {cities.map((city, i) => {
        const [cx, cy] = xy(...city.c);
        // [offsetX, offsetY, textAnchor] — evita sobreposição entre labels
        const offsets = {
          'Ariquemes': [25, 15, 'start'],  // HQ, acima
          'Porto Velho': [1, -1, 'end'],  // esq, acima
          'Candeias do Jamari': [-3, 5, 'start'],  // dir, acima
          'Itapuã do Oeste': [30, 10, 'end'],  // esq
          'Rio Crespo': [35, 15, 'start'],  // esq, abaixo Itapuã
          'Machadinho D\'Oeste': [20, 10, 'start'],  // dir
          'Cujubim': [25, 5, 'start'],  // dir, acima Machadinho
          'Alto Paraíso': [25, 10, 'end'],  // esq
          'Monte Negro': [15, 15, 'end'],  // esq
          'Campo Novo de Rondônia': [10, 20, 'end'],  // esq extremo
          'Buritis': [2, 20, 'end'],  // esq, acima Monte Negro
          'Cacaulândia': [15, 18, 'start'],  // dir
          'Jaru': [25, 20, 'start'],  // dir
          'Ji-Paraná': [25, 20, 'start'],  // dir
          'Pres. Médici': [15, 20, 'start'],  // dir
          'Pimenta Bueno': [30, 18, 'start'],  // dir, acima Rolim
          'Rolim de Moura': [15, 20, 'start'],  // dir
          'Cabixi': [25, 8, 'start'],  // isolado sul
          // ── Amazonas ──
          'Manicoré': [45, 0, 'end'],            // esq, abaixo
          'Apuí': [4, 2, 'start'],               // dir, abaixo
          'Boca do Acre': [10, 0, 'end'],        // esq, abaixo
          'Novo Aripuanã': [4, 8, 'start'],     // dir, acima
          // ── Pará ──
          'Itaituba': [4, 5, 'start'],           // dir, abaixo
          // ── Mato Grosso ──
          'Lambari do Oeste': [15, 0, 'start'],   // dir, abaixo
          // ── Bahia ──
          'Mascote': [4, 5, 'start'],            // dir, abaixo
        };
        const [ox, oy, anchor] = offsets[city.n] || [4, 3, 'start'];
        // Ponto E label se movem juntos — offsets escalam junto com o tamanho da fonte
        const px = cx + ox * scale;
        const py = cy + (oy - 1) * scale;
        // Posição do texto (ao lado do ponto deslocado)
        const tx = anchor === 'end' ? px - 2 * scale : px + 2 * scale;
        const ty = py + 1.5 * scale;
        return (
          <g key={i}>
            {city.hq && (
              <circle cx={px} cy={py} r={hqRingR} fill="none" stroke="#F26B22" strokeWidth={hqStroke} opacity={0.5} />
            )}
            <circle cx={px} cy={py} r={city.hq ? hqR : dotR} fill={city.hq ? '#F26B22' : '#0C1A2E'} />
            <text x={tx} y={ty}
              textAnchor={anchor}
              style={{ font: `400 ${fontPx}px Outfit,sans-serif`, fill: city.hq ? '#F26B22' : '#0C1A2E' }}
            >{city.n}</text>
          </g>
        );
      })}
    </svg>
  );
}

export default function BrazilMap() {
  const svgRef = useRef(null);
  const groupsRef = useRef({});
  const currentUfRef = useRef(null);
  const wrapRef = useReveal();

  const txt = useBloco('home.map');
  const mapPresence = useMapa();
  const PRESENCE_UFS = new Set(Object.keys(mapPresence));

  const [panelContent, setPanelContent] = useState(null);
  const [zoomState, setZoomState] = useState(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    svg.querySelectorAll('path[data-uf]').forEach(path => {
      const uf = path.dataset.uf;
      // O agrupamento só é criado uma vez; em re-render o grupo
      // já existe e apenas a classe de presença é atualizada.
      let g = groupsRef.current[uf];
      if (!g) {
        g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        g.classList.add('state-group');
        g.dataset.uf = uf;
        path.parentNode.insertBefore(g, path);
        g.appendChild(path);
        groupsRef.current[uf] = g;
      }
      g.classList.toggle('has-presence', PRESENCE_UFS.has(uf));
    });

    const showState = (uf) => {
      Object.values(groupsRef.current).forEach(g => {
        g.classList.toggle('hovered', g.dataset.uf === uf);
        if (g.dataset.uf === uf) g.parentNode.appendChild(g);
      });
      svg.querySelectorAll('path').forEach(p => p.classList.toggle('hovered', p.dataset.uf === uf));

      const data = mapPresence[uf];
      const pathData = BRAZIL_SVG_PATHS.find(p => p.uf === uf);
      setPanelContent(data || null);
      setZoomState(data && pathData ? { uf, d: pathData.d, cities: data.cities, bbox: getPathBBox(pathData.d) } : null);
    };

    const clear = () => {
      currentUfRef.current = null;
      Object.values(groupsRef.current).forEach(g => g.classList.remove('hovered'));
      svg.querySelectorAll('path').forEach(p => p.classList.remove('hovered'));
      setPanelContent(null);
      setZoomState(null);
    };

    const onMove = (e) => {
      const path = e.target.closest('path[data-uf]');
      const uf = path ? path.dataset.uf : null;
      if (uf !== currentUfRef.current) { currentUfRef.current = uf; uf ? showState(uf) : clear(); }
    };

    // Toque/clique: essencial no mobile, onde não existe hover
    const onClick = (e) => {
      const path = e.target.closest('path[data-uf]');
      const uf = path ? path.dataset.uf : null;
      if (uf && uf === currentUfRef.current) {
        currentUfRef.current = null;
        clear();
      } else if (uf) {
        currentUfRef.current = uf;
        showState(uf);
      }
    };

    const onLeave = () => { currentUfRef.current = null; clear(); };

    svg.addEventListener('mousemove', onMove);
    svg.addEventListener('mouseleave', onLeave);
    svg.addEventListener('click', onClick);
    return () => {
      svg.removeEventListener('mousemove', onMove);
      svg.removeEventListener('mouseleave', onLeave);
      svg.removeEventListener('click', onClick);
    };
    // Depende do mapa: quando as cidades mudam no painel, os
    // handlers precisam enxergar a lista nova.
  }, [mapPresence]);

  return (
    <div id="mapa-outer">
      <section id="mapa">
        <div className="lbl reveal" ref={wrapRef}>{txt.eyebrow}</div>

        <div className="map-wrap">
          {/* ── Info lateral ── */}
          <div className="map-info">
            <h2>{txt.heading} <em>{txt.headingEm}</em></h2>
            <p className="lead">{txt.lead}</p>
            <div className="state-panel">
              {panelContent ? (
                <>
                  <div className="sp-name">{panelContent.label}</div>
                  {panelContent.desc && <div className="sp-desc">{panelContent.desc}</div>}
                  <ul className="sp-cities">
                    {panelContent.cities.map((c, i) => (
                      <li key={i} className={c.hq ? 'hq' : ''}>{c.n}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="sp-hint">{txt.hint}</p>
              )}
            </div>
          </div>

          {/* ── Área do mapa ── */}
          <div className="map-area">
            {/* Mapa principal */}
            <div className="map-main">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                id="mapSvg"
                ref={svgRef}
                viewBox="0 0 700 700"
                style={{ width: '100%', overflow: 'visible' }}
              >
                <g id="brasil">
                  {BRAZIL_SVG_PATHS.map(({ uf, d, hasPresence }) => (
                    <path key={uf} data-uf={uf} className={hasPresence ? 'has-presence' : ''} d={d} />
                  ))}
                </g>
              </svg>
            </div>

            {/* Painel de zoom - aparece à direita */}
            <div className={`map-zoom-panel ${zoomState ? 'active' : ''}`}>
              <ZoomPanel zoomState={zoomState} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}